import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import apiService from "../../services/api";
import { formatTimeAgo } from "../../utils/time";
import TablePagination from "../company/JobManagement/Components/TablePagination";

interface Notification {
  id: string;
  type: string;
  priority: string;
  title: string;
  message: string;
  action_url?: string;
  action_text?: string;
  is_read: boolean;
  created_at: string;
  read_at?: string;
  company_name?: string;
  job_title?: string;
}

interface NotificationResponse {
  notifications: Notification[];
  total: number;
  unread_count: number;
  has_more: boolean;
}

const CandidateNotifications: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] =
    useState<NotificationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  //const [pageSize] = useState(10);

  const loadNotifications = async (
    page: number = 1,
    unreadOnly: boolean = false,
  ) => {
    try {
      setLoading(true);
      const skip = (page - 1) * rowsPerPage;
      const response = await apiService.getCandidateNotifications(
        skip,
        rowsPerPage,
        unreadOnly,
      );
      setNotifications(response);
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications(currentPage, filter === "unread");
  }, [currentPage, filter, rowsPerPage]);

  const handleToggleRead = async (
    notificationId: string,
    isCurrentlyRead: boolean,
  ) => {
    try {
      if (isCurrentlyRead) {
        // Mark as unread - we'll implement this API endpoint
        await apiService.markNotificationUnread(notificationId);
      } else {
        // Mark as read
        await apiService.markNotificationRead(notificationId);
      }
      await loadNotifications(currentPage, filter === "unread");
    } catch (error) {
      console.error("Error toggling notification read status:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsRead();
      await loadNotifications(currentPage, filter === "unread");
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "text-red-600 bg-red-50 border-red-200";
      case "high":
        return "text-orange-600 bg-orange-50 border-orange-200";
      case "medium":
        return "text-blue-600 bg-blue-50 border-blue-200";
      case "low":
        return "text-gray-600 bg-gray-50 border-gray-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "recruiter_message":
        return "💬";
      case "interview_invitation":
        return "📅";
      case "profile_view":
        return "👁️";
      case "resume_download":
        return "📄";
      case "job_match":
        return "🎯";
      case "application_update":
        return "📋";
      case "skill_verification":
        return "🏆";
      case "background_check":
        return "✅";
      default:
        return "📢";
    }
  };

  // Truncate message to make it brief
  const truncateMessage = (message: string, maxLength: number = 60) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  const totalPages = notifications
    ? Math.ceil(notifications.total / rowsPerPage)
    : 0;

  if (loading && !notifications) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const handleOpenNotification = async (notification: Notification) => {
    try {
      // If already read → do nothing
      if (notification.is_read) return;

      // Optimistic UI update (instant)
      setNotifications((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          notifications: prev.notifications.map((n) =>
            n.id === notification.id ? { ...n, is_read: true } : n,
          ),
          unread_count: prev.unread_count - 1,
        };
      });

      // Call API
      await apiService.markNotificationRead(notification.id);
    } catch (error) {
      console.error("Error opening notification:", error);
    }
  };
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Notifications
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            Stay updated with your job search activity
          </p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">
                  Filter:
                </label>
                <select
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value as "all" | "unread");
                    setCurrentPage(1);
                  }}
                  className="min-w-[11rem] border border-gray-300 rounded-md pl-3 pr-8 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="all">All notifications</option>
                  <option value="unread">Unread only</option>
                </select>
              </div>
              {notifications && (
                <div className="text-sm text-gray-600">
                  {notifications.total} total • {notifications.unread_count}{" "}
                  unread
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {notifications && notifications.unread_count > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Mark all as read
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Notifications List */}
        {notifications && notifications.notifications.length > 0 ? (
          <>
            <div className="space-y-3 mb-8">
              {notifications.notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleOpenNotification(notification)}
                  className={`cursor-pointer bg-white border rounded-lg p-4 transition-all hover:shadow-sm ${
                    notification.is_read
                      ? "border-gray-200"
                      : "border-emerald-200 bg-emerald-50/30"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center space-x-3 flex-1">
                      <span className="text-lg">{getTypeIcon(notification.type)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3
                            className={`font-semibold text-sm truncate ${
                              notification.is_read
                                ? "text-gray-700"
                                : "text-gray-900"
                            }`}
                          >
                            {notification.title}
                          </h3>
                          <span
                            className={`text-xs px-2 py-1 rounded-full border flex-shrink-0 ${getPriorityColor(
                              notification.priority,
                            )}`}
                          >
                            {notification.priority}
                          </span>
                          {!notification.is_read && (
                            <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0"></span>
                          )}
                        </div>

                        {/* Brief message */}
                        <p className={`text-xs ${notification.is_read ? 'text-gray-500' : 'text-gray-600'} mb-1`}>
                          {truncateMessage(notification.message)}
                        </p>

                        <div className="flex items-center space-x-3 text-xs text-gray-400">
                          <span>{formatTimeAgo(notification.created_at)}</span>
                          {notification.company_name && (
                            <span>from {notification.company_name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 sm:ml-4">
                      {notification.action_text && notification.action_url && (
                        <a
                          href={notification.action_url}
                          className="text-xs text-emerald-600 hover:text-emerald-700 font-medium px-2 py-1 rounded border border-emerald-200 hover:bg-emerald-50 transition-colors"
                        >
                          {notification.action_text}
                        </a>
                      )}
                      {!notification.is_read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); 
                            handleToggleRead(
                              notification.id,
                              notification.is_read,
                            );
                          }}
                          className="text-xs px-2 py-1 rounded border text-emerald-600 hover:text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        >
                          Mark as read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              
              <TablePagination
                currentPage={currentPage}
                totalCount={notifications.total}
                rowsPerPage={rowsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
                onRowsPerPageChange={(rows) => {
                 setRowsPerPage(rows);
                 setCurrentPage(1);
                }}
              />
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div className="text-gray-400 text-6xl mb-6">🔔</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {filter === "unread"
                ? "No unread notifications"
                : "No notifications yet"}
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              {filter === "unread"
                ? "All caught up! You have no unread notifications at the moment."
                : "You'll receive notifications when recruiters view your profile, send messages, or when there are job matches."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateNotifications; 
